import * as yup from 'yup'
import { TFunction } from 'react-i18next'

import { isValidWebsiteURL } from '@percent/utility'

const DEFAULT_MAX_LENGTH = 255
const EMAIL_COPY_MAX_LENGTH = 3000

export const validationSchema = (t: TFunction) => {
  return yup.object().shape({
    programName: yup
      .string()
      .max(
        DEFAULT_MAX_LENGTH,
        t('errorMessage.maxLength', { fieldName: t('form.nameOfTheProgram'), maxCharacterLimit: DEFAULT_MAX_LENGTH })
      ),
    allowedOrganisationTypes: yup.array().of(yup.string()).min(1, t('errorMessage.selectAtLeastOne')),
    disallowedOrganisations: yup.array().of(
      yup.object().shape({
        organisationId: yup.string().nullable(),
        type: yup.string().nullable(),
        relationship: yup.string().nullable()
      })
    ),
    primaryColor: yup.string().test('is-hex-color', t('errorMessage.invalidHexColor'), value => {
      if (!value) {
        return true
      }

      return /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)
    }),
    acceptanceEmailCopy: yup.string().max(
      EMAIL_COPY_MAX_LENGTH,
      t('errorMessage.maxLength', {
        fieldName: t('form.acceptanceEmailCopy'),
        maxCharacterLimit: EMAIL_COPY_MAX_LENGTH
      })
    ),
    rejectionEmailCopy: yup.string().max(
      EMAIL_COPY_MAX_LENGTH,
      t('errorMessage.maxLength', {
        fieldName: t('form.rejectionEmailCopy'),
        maxCharacterLimit: EMAIL_COPY_MAX_LENGTH
      })
    ),
    programWebsite: isValidWebsiteURL()
      .max(
        DEFAULT_MAX_LENGTH,
        t('errorMessage.maxLength', { fieldName: t('form.programWebsite'), maxCharacterLimit: DEFAULT_MAX_LENGTH })
      )
      .trim(),
    partnerFields: yup.array().of(
      yup
        .object()
        .shape({
          title: yup.string(),
          id: yup.string(),
          type: yup.string().nullable(),
          placeholder: yup.string().nullable(),
          validation: yup.object().shape({
            required: yup.boolean()
          }),
          options: yup.array().of(yup.object().shape({ key: yup.string(), value: yup.string() }))
        })
        .test(
          'both-or-none',
          'Both ID and Title are required if either is filled',
          function validateRequiredFields(value) {
            const idFilled = !!value?.id?.trim()
            const titleFilled = !!value?.title?.trim()
            const typeSelected = !!value?.type

            if (
              (idFilled && (!titleFilled || !typeSelected)) ||
              (titleFilled && (!idFilled || !typeSelected)) ||
              (typeSelected && (!idFilled || !titleFilled))
            ) {
              return this.createError({
                path: 'partnerFields'
              })
            }

            return true
          }
        )
        .test(
          'options-required',
          'At least one option is required for multiple choice and select fields',
          function validateFieldOptions(value) {
            const type = value?.type
            const isMultipleChoiceOrSelect = type === 'multiple_choice' || type === 'select'

            if (isMultipleChoiceOrSelect && value?.options?.some(option => !option.key || !option.value)) {
              return this.createError({
                path: 'partnerFields'
              })
            }

            return true
          }
        )
    )
  })
}
